import { formatExpectedMetric } from "../../eventMetrics";
import { aggregateExpectedGoals, isPenaltyDetectionReliable } from "../../expectedGoalsEvents";
import type { GamePlayerStatColumnEnum, GPEventResponse, GPGamePlayerResponse } from "../../types";
import { PlayerStatCalculationResult, PlayerStatType } from "./definition";
import { createBaseResult, parseNumericValue, type GamePlayerStatLookup } from "./precomputedGoalkeeperStats";

const EXPECTED_GOALS_COLUMN_BY_STAT_TYPE: Partial<Record<PlayerStatType, GamePlayerStatColumnEnum>> = {
	[PlayerStatType.TotalXg]: "totalXg",
	[PlayerStatType.TotalXa]: "totalXa",
	[PlayerStatType.NonPenaltyXg]: "nonPenaltyXg",
	[PlayerStatType.XgPerShot]: "nonPenaltyXgPerShot",
};

const expectedGoalsColumnTypes = new Set<GamePlayerStatColumnEnum>(
	Object.values(EXPECTED_GOALS_COLUMN_BY_STAT_TYPE).filter(
		(column): column is GamePlayerStatColumnEnum => column !== undefined,
	),
);

export function hasStoredPlayerExpectedGoals(lookup: GamePlayerStatLookup): boolean {
	return [...lookup.values()].some((columns) =>
		[...expectedGoalsColumnTypes].some((column) => parseNumericValue(columns.get(column) ?? null) !== null),
	);
}

export function formatStoredExpectedGoalsStat(
	statType: PlayerStatType,
	player: GPGamePlayerResponse,
	lookup: GamePlayerStatLookup,
): PlayerStatCalculationResult {
	const result = createBaseResult(player, statType);
	const column = EXPECTED_GOALS_COLUMN_BY_STAT_TYPE[statType];

	if (column === undefined || player.id === null) {
		return result;
	}

	const metric = parseNumericValue(lookup.get(player.id)?.get(column) ?? null);

	if (metric === null) {
		return result;
	}

	result.value = metric;
	result.valueFormatted = formatExpectedMetric(metric);
	result.valueSort = metric;
	result.isUseful = metric !== 0;

	return result;
}

function toStoredValue(metric: number | null): string | null {
	if (metric === null) {
		return null;
	}

	return String(metric);
}

function buildPeriodFilteredLookup(
	gamePlayerId: number,
	periods: string[],
	events: GPEventResponse[],
): GamePlayerStatLookup {
	const aggregate = aggregateExpectedGoals(events, periods, (event) => event.gamePlayerId === gamePlayerId);
	const canExcludePenalties = isPenaltyDetectionReliable(events, periods);
	const hasNonPenaltyShots = aggregate.nonPenaltyShotsWithExpectedGoals > 0;
	const expectedGoalsPerShot = canExcludePenalties && hasNonPenaltyShots
		? aggregate.nonPenaltyExpectedGoals / aggregate.nonPenaltyShotsWithExpectedGoals
		: null;

	const columnMap = new Map<GamePlayerStatColumnEnum, string | null>();
	columnMap.set("totalXg", toStoredValue(aggregate.expectedGoals));
	columnMap.set("totalXa", toStoredValue(aggregate.expectedAssists));
	columnMap.set("nonPenaltyXg", toStoredValue(canExcludePenalties ? aggregate.nonPenaltyExpectedGoals : null));
	columnMap.set("nonPenaltyXgPerShot", toStoredValue(expectedGoalsPerShot));

	const lookup: GamePlayerStatLookup = new Map();
	lookup.set(gamePlayerId, columnMap);

	return lookup;
}

export function calculatePeriodFilteredExpectedGoalsStat(
	statType: PlayerStatType,
	player: GPGamePlayerResponse,
	periods: string[],
	events: GPEventResponse[],
): PlayerStatCalculationResult {
	if (player.id === null) {
		return formatStoredExpectedGoalsStat(statType, player, new Map());
	}

	return formatStoredExpectedGoalsStat(
		statType,
		player,
		buildPeriodFilteredLookup(player.id, periods, events),
	);
}
